import type { AxiosResponse } from 'axios'
import { apiClient } from '../client'
import { API_ENDPOINTS } from '@/config/api'
import { handleApiError } from '@/utils/apiErrorHandling'
import type {
  CallerHistoryEntry,
  CallerHistoryParams,
  NormalizedCallerHistoryEntry,
  SoftphoneActiveCallsResponse,
  SoftphoneActiveCallsResult,
  SoftphoneCallResponse,
  SoftphoneCallSession,
  SoftphoneCallerHistoryResponse,
  SoftphoneCredentials,
  SoftphoneCredentialsResponse,
  SoftphoneHealthResponse,
  SoftphoneHistoryParams,
  SoftphoneHistoryResponse,
  SoftphoneQueueStatus,
  SoftphoneQueueStatusResponse,
  SoftphoneStartCallRequest,
  SoftphoneStartCallResponse,
  SoftphoneStatistics,
  SoftphoneStatisticsResponse,
} from '@/types/api/softphone'
import { callTypeOptions } from '@/components/cases/caseConstants'

const extractCalls = (value: unknown): SoftphoneCallSession[] => {
  if (!value || typeof value !== 'object') return []
  const data = value as {
    calls?: SoftphoneCallSession[]
    items?: SoftphoneCallSession[]
    data?: {
      calls?: SoftphoneCallSession[]
      items?: SoftphoneCallSession[]
    }
  }

  return data.calls || data.items || data.data?.calls || data.data?.items || []
}

const extractCall = (value: unknown): SoftphoneCallSession | null => {
  if (!value || typeof value !== 'object') return null
  const data = value as {
    call?: SoftphoneCallSession
    data?: {
      call?: SoftphoneCallSession
    }
  }

  return data.call || data.data?.call || null
}

const extractCallerHistoryEntries = (value: unknown): CallerHistoryEntry[] => {
  if (!value || typeof value !== 'object') return []
  const data = value as {
    calls?: CallerHistoryEntry[]
    items?: CallerHistoryEntry[]
    history?: CallerHistoryEntry[]
    data?: {
      calls?: CallerHistoryEntry[]
      items?: CallerHistoryEntry[]
      history?: CallerHistoryEntry[]
    }
  }

  return (
    data.calls ||
    data.items ||
    data.history ||
    data.data?.calls ||
    data.data?.items ||
    data.data?.history ||
    []
  )
}

const pickFirstString = (...candidates: Array<unknown>): string | undefined => {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim()
    }
  }
  return undefined
}

const resolveDispatcherName = (
  entry: CallerHistoryEntry
): string | undefined => {
  if (typeof entry.dispatcher === 'string' && entry.dispatcher.trim()) {
    return entry.dispatcher.trim()
  }

  if (entry.dispatcher && typeof entry.dispatcher === 'object') {
    const named = entry.dispatcher.name?.trim()
    if (named) return named

    const first = entry.dispatcher.first_name?.trim() || ''
    const last = entry.dispatcher.last_name?.trim() || ''
    const combined = `${first} ${last}`.trim()
    if (combined) return combined
  }

  return pickFirstString(entry.dispatcher_name, entry.actor_name)
}

const resolveNote = (entry: CallerHistoryEntry): string | undefined => {
  if (Array.isArray(entry.notes)) {
    const joined = entry.notes
      .filter(
        (note): note is string => typeof note === 'string' && !!note.trim()
      )
      .join(' ')
      .trim()
    if (joined) return joined
  }

  if (typeof entry.notes === 'string' && entry.notes.trim()) {
    return entry.notes.trim()
  }

  return pickFirstString(
    entry.note,
    entry.summary,
    entry.closing_note,
    entry.description
  )
}

const formatCaseTypeLabel = (raw?: string): string | undefined => {
  if (!raw) return undefined

  const matched = callTypeOptions.find(
    option =>
      option.value === raw || option.label.toLowerCase() === raw.toLowerCase()
  )
  if (matched) return matched.label

  return raw.replace(/[_-]/g, ' ').replace(/\b\w/g, char => char.toUpperCase())
}

const formatCallOutcome = (status?: string): string | undefined => {
  if (!status) return undefined

  const normalized = status.trim().toLowerCase()
  const labels: Record<string, string> = {
    completed: 'Call Completed',
    missed: 'Missed Call',
    no_answer: 'No Answer',
    rejected: 'Call Rejected',
    cancelled: 'Call Cancelled',
    canceled: 'Call Cancelled',
    failed: 'Call Failed',
    answered: 'Call Answered',
    ringing: 'Ringing',
    dialing: 'Dialing',
    held: 'On Hold',
    transferring: 'Transferring',
  }

  if (labels[normalized]) return labels[normalized]

  return status
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase())
}

export const normalizeCallerHistoryEntry = (
  entry: CallerHistoryEntry,
  index: number
): NormalizedCallerHistoryEntry => {
  const id =
    pickFirstString(entry.call_id, entry.case_id, entry.caseId, entry.id) ||
    `caller-history-${index}`

  const explicitOutcome = pickFirstString(
    entry.outcome,
    entry.disposition,
    entry.closing_reason
  )

  return {
    id,
    dateTime: pickFirstString(
      entry.started_at,
      entry.created_at,
      entry.createdAt,
      entry.answered_at,
      entry.case_created_time,
      entry.updated_at,
      entry.updatedAt
    ),
    caseType: formatCaseTypeLabel(
      pickFirstString(
        entry.call_type,
        entry.case_type,
        entry.incident_type,
        entry.category
      )
    ),
    dispatcherId: pickFirstString(entry.assigned_to_actor_id, entry.actor_id),
    dispatcherName: resolveDispatcherName(entry),
    outcome: explicitOutcome || formatCallOutcome(entry.status),
    note: resolveNote(entry),
    raw: entry,
  }
}

const extractCredentials = (
  value: SoftphoneCredentialsResponse
): SoftphoneCredentials => {
  const candidate = value.data?.credentials || value.data

  if (
    !candidate?.username ||
    !candidate?.password ||
    !candidate?.sip_domain ||
    !candidate?.wss_uri
  ) {
    throw new Error('Softphone credentials response is incomplete')
  }

  return {
    username: candidate.username,
    password: candidate.password,
    sip_domain: candidate.sip_domain,
    wss_uri: candidate.wss_uri,
    ice_servers: candidate.ice_servers,
    iceServers: candidate.iceServers,
    expires_at: candidate.expires_at,
  }
}

const extractQueueStatus = (
  value: SoftphoneQueueStatusResponse
): SoftphoneQueueStatus => {
  const data = value.data || {}
  const queue = data.queue || (data as Partial<SoftphoneQueueStatus>)
  const callers = queue.callers || data.callers || []
  const callerCount =
    queue.callerCount ?? data.callerCount ?? data.caller_count ?? callers.length
  const dispatcherCount =
    queue.dispatcherCount ?? data.dispatcherCount ?? data.dispatcher_count ?? 0
  const dispatcherPosition =
    queue.dispatcherPosition ??
    data.dispatcherPosition ??
    data.dispatcher_position

  return {
    name: queue.name || data.name,
    callers,
    callerCount,
    dispatcherCount,
    dispatcherPosition,
    agents: queue.agents || data.agents,
    registeredDispatchers:
      queue.registeredDispatchers || data.registeredDispatchers,
    checkedAt: queue.checkedAt || data.checkedAt,
    degraded: queue.degraded || data.degraded,
    error: queue.error || data.error,
  }
}

const normalizeStartCallData = (
  data: SoftphoneStartCallResponse['data']
): SoftphoneStartCallResponse['data'] => {
  if (data.dial || !data.call) return data

  const target = data.call.dial_target || data.call.sip_uri
  if (!target) return data

  return {
    ...data,
    dial: {
      to_type: data.call.to_type || null,
      target,
      sip_uri: data.call.sip_uri,
    },
  }
}

class SoftphoneService {
  async getCredentials(): Promise<SoftphoneCredentials> {
    try {
      const response: AxiosResponse<SoftphoneCredentialsResponse> =
        await apiClient.get(API_ENDPOINTS.SOFTPHONE_CREDENTIALS)

      return extractCredentials(response.data)
    } catch (error) {
      throw handleApiError(error, 'getSoftphoneCredentials')
    }
  }

  async startUserCall(
    payload: SoftphoneStartCallRequest,
    idempotencyKey: string
  ): Promise<SoftphoneStartCallResponse['data']> {
    try {
      const response: AxiosResponse<SoftphoneStartCallResponse> =
        await apiClient.post(API_ENDPOINTS.CALLS_START, payload, {
          headers: {
            'Idempotency-Key': idempotencyKey,
          },
        })

      return normalizeStartCallData(response.data.data)
    } catch (error) {
      throw handleApiError(error, 'startSoftphoneCall')
    }
  }

  async acceptCall(callId: string): Promise<SoftphoneCallSession | null> {
    try {
      const response: AxiosResponse<SoftphoneCallResponse> =
        await apiClient.post(API_ENDPOINTS.CALL_ACCEPT(callId))

      return extractCall(response.data)
    } catch (error) {
      throw handleApiError(error, 'acceptSoftphoneCall')
    }
  }

  async rejectCall(
    callId: string,
    reason?: string
  ): Promise<SoftphoneCallSession | null> {
    try {
      const response: AxiosResponse<SoftphoneCallResponse> =
        await apiClient.post(API_ENDPOINTS.CALL_REJECT(callId), {
          ...(reason && { reason }),
        })

      return extractCall(response.data)
    } catch (error) {
      throw handleApiError(error, 'rejectSoftphoneCall')
    }
  }

  async hangUpCall(callId: string): Promise<SoftphoneCallSession | null> {
    try {
      const response: AxiosResponse<SoftphoneCallResponse> =
        await apiClient.post(API_ENDPOINTS.CALL_HANGUP(callId))

      return extractCall(response.data)
    } catch (error) {
      throw handleApiError(error, 'hangUpSoftphoneCall')
    }
  }

  async holdCall(callId: string): Promise<SoftphoneCallSession | null> {
    try {
      const response: AxiosResponse<SoftphoneCallResponse> =
        await apiClient.post(API_ENDPOINTS.CALL_HOLD(callId))

      return extractCall(response.data)
    } catch (error) {
      throw handleApiError(error, 'holdSoftphoneCall')
    }
  }

  async unholdCall(callId: string): Promise<SoftphoneCallSession | null> {
    try {
      const response: AxiosResponse<SoftphoneCallResponse> =
        await apiClient.post(API_ENDPOINTS.CALL_UNHOLD(callId))

      return extractCall(response.data)
    } catch (error) {
      throw handleApiError(error, 'unholdSoftphoneCall')
    }
  }

  async transferCall(
    callId: string,
    destination: string,
    transferType: 'blind' | 'attended' = 'blind'
  ): Promise<SoftphoneCallSession | null> {
    try {
      const response: AxiosResponse<SoftphoneCallResponse> =
        await apiClient.post(API_ENDPOINTS.CALL_TRANSFER(callId), {
          destination,
          transferType,
        })

      return extractCall(response.data)
    } catch (error) {
      throw handleApiError(error, 'transferSoftphoneCall')
    }
  }

  async getCall(callId: string): Promise<SoftphoneCallSession | null> {
    try {
      const response: AxiosResponse<SoftphoneCallResponse> =
        await apiClient.get(API_ENDPOINTS.CALL_BY_ID(callId))

      return extractCall(response.data)
    } catch (error) {
      throw handleApiError(error, 'getSoftphoneCall')
    }
  }

  async listActiveCalls(limit = 50): Promise<SoftphoneActiveCallsResult> {
    try {
      const response: AxiosResponse<SoftphoneActiveCallsResponse> =
        await apiClient.get(API_ENDPOINTS.CALLS_ACTIVE, {
          params: { limit },
        })

      return {
        calls: extractCalls(response.data),
        queue: response.data.data?.queue
          ? extractQueueStatus({
              status: response.data.status,
              data: response.data.data.queue,
            })
          : undefined,
        count: response.data.data?.count,
      }
    } catch (error) {
      throw handleApiError(error, 'listActiveSoftphoneCalls')
    }
  }

  async getQueueStatus(dispatcherId?: string): Promise<SoftphoneQueueStatus> {
    try {
      const response: AxiosResponse<SoftphoneQueueStatusResponse> =
        await apiClient.get(API_ENDPOINTS.CALLS_QUEUE_STATUS, {
          params: {
            dispatcher_id: dispatcherId,
          },
        })

      return extractQueueStatus(response.data)
    } catch (error) {
      throw handleApiError(error, 'getSoftphoneQueueStatus')
    }
  }

  async getCallHistory(params: SoftphoneHistoryParams = {}): Promise<{
    calls: SoftphoneCallSession[]
    nextToken?: string
    lastKey?: string
  }> {
    try {
      const response: AxiosResponse<SoftphoneHistoryResponse> =
        await apiClient.get(API_ENDPOINTS.CALLS_HISTORY, {
          params: {
            limit: params.limit || 50,
            actor_id: params.actor_id,
            case_id: params.case_id,
            phone_number: params.phone_number,
            lastKey: params.lastKey,
          },
        })

      return {
        calls: extractCalls(response.data),
        nextToken: response.data.data?.nextToken,
        lastKey: response.data.data?.lastKey || response.data.data?.nextToken,
      }
    } catch (error) {
      throw handleApiError(error, 'getSoftphoneCallHistory')
    }
  }

  async getCallerHistory(params: CallerHistoryParams): Promise<{
    entries: NormalizedCallerHistoryEntry[]
    nextToken?: string
    lastKey?: string
  }> {
    try {
      const response: AxiosResponse<SoftphoneCallerHistoryResponse> =
        await apiClient.get(API_ENDPOINTS.CALLS_HISTORY, {
          params: {
            phone_number: params.phone_number,
            actor_id: params.actor_id,
            limit: params.limit || 50,
            lastKey: params.lastKey,
          },
        })

      const entries = extractCallerHistoryEntries(response.data).map(
        (entry, index) => normalizeCallerHistoryEntry(entry, index)
      )

      const nextToken = response.data.data?.nextToken
      const lastKey = response.data.data?.lastKey || nextToken

      return {
        entries,
        nextToken: nextToken == null ? undefined : nextToken,
        lastKey: lastKey == null ? undefined : lastKey,
      }
    } catch (error) {
      throw handleApiError(error, 'getCallerHistory')
    }
  }

  async getStatistics(): Promise<SoftphoneStatistics> {
    try {
      const response: AxiosResponse<SoftphoneStatisticsResponse> =
        await apiClient.get(API_ENDPOINTS.CALLS_STATISTICS)

      return (
        response.data.data?.statistics ||
        response.data.data?.counts ||
        (response.data.data as SoftphoneStatistics) ||
        {}
      )
    } catch (error) {
      throw handleApiError(error, 'getSoftphoneStatistics')
    }
  }

  async getHealth(): Promise<SoftphoneHealthResponse['data']> {
    try {
      const response: AxiosResponse<SoftphoneHealthResponse> =
        await apiClient.get(API_ENDPOINTS.CALLS_HEALTH, {
          headers: { 'skip-auth': true },
        })

      return response.data.data
    } catch (error) {
      throw handleApiError(error, 'getSoftphoneHealth')
    }
  }
}

export const softphoneService = new SoftphoneService()
export default softphoneService
